<template>
	<div
		class="sw-language-switcher flex lg:items-center lg:justify-center items-left"
		data-testid="language-switcher"
	>
		<ElSelectExtended
			id="language"
			class="lg:w-auto w-full flex items-center"
			:flags="true"
			:model-value="{
				language: localeProperties.code || localeProperties.iso,
				currency: norceStore.currencyId
			}"
			:title1="languageTitle"
			:options1="cultures"
			:title2="currencyTitle"
			:options2="currencies"
			:title3="regionTitle"
			:options3="alternatives"
			:is-transparent-header="isTransparentHeader"
			@update:modelValue="updateLanguageAndCurrency"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, defineProps, computed, inject } from "vue";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type {
	CulturesNorce,
	AlternativesNorce
} from "~/types/components/header";
import type { Locale } from "~/types/locale";
import ElSelectExtended from "../../components/Elements/ElSelectExtended.vue";

interface Props {
	cultures: CulturesNorce[] | undefined;
	currencies: Currency[] | undefined;
	isTransparentHeader?: boolean;
}
const { cultures, currencies } = defineProps<Props>();

const { locales, localeProperties, t } = useI18n();

const alternatives = computed<AlternativesNorce[]>(() => {
	const rawAlternatives = (locales.value?.[0] as Locale)?.alternatives || [];
	const currentDomain = window?.location?.hostname;
	return rawAlternatives.filter(
		(alt: AlternativesNorce) => alt.value !== currentDomain
	);
});
const forceRender = inject<() => Promise<void>>("forceRender")!;

const norceStore = useNorceStore();

const { currencyId } = storeToRefs(useNorceStore());

const languageTitle = computed(() => {
	return t("Language");
});

const currencyTitle = computed(() => {
	return t("Currencies");
});

const regionTitle = computed(() => {
	return t("Country");
});

const selectedValues = ref<{ language: string; currency: number }>();
const i18nRedirected = useCookie<string>("i18n_redirected");

const updateLanguageAndCurrency = (values: {
	language: string;
	currency: number;
}) => {
	const { language, currency } = values;
	const selectedCulture = cultures?.find(
		(culture) => (culture.code ?? culture.value) === language
	);
	const cultureCode = selectedCulture?.value ?? language;
	const codeForUrl = selectedCulture?.code ?? language;

	selectedValues.value = values;

	norceStore.setCultureCode(cultureCode);
	norceStore.setLocaleCode(codeForUrl);
	norceStore.setSalesAreaId(undefined);
	norceStore.updateCurrency(currency);
	i18nRedirected.value = codeForUrl;
	window.location.href = `${window.location.origin}/${codeForUrl}`;
};

watch(currencyId, async () => {
	await forceRender();
});
</script>
